import { INSIConnector } from '@saas/lib/dmp/web'
import CryptoJS from 'crypto-js'
import { Patient } from '@saas/interfaces/graphql'
import { GetCurrentUserResponseIds, getIdsConfig, getIdsHealthcareservicesHcsId } from '@toktokdoctech/tokapi-client'
import TTDError from './ttdError'
import dcparametersStaging from '../config/dcparameters.staging.json'
import dcparametersProduction from '../config/dcparameters.production.json'
import packageJson from '../../../package.json'
import {
    isErrorOnFirstCharacter,
    isSpaceOrApostropheDoubledOrCombinedInString,
    cleanAccentsAndDiacritics,
} from './stringUtils'

const packageVersion = packageJson.version

/**
 * @description Looks if the value is valid for being sended to the INSIConnector
 * @param {string} type it can be 'string' or 'array'
 * @param {string} value it can be a string passed to be check or an array
 * @returns {Boolean}
 */
const isFormatValid = (type: any, value: any) => {
    const format = /[^\w '-]+/ // Here there are special characters not accepted, we accept ' and -
    if (type === 'array') {
        if (value && value.length > 0) {
            for (let i = 0; i < value.length; i++) {
                if (value[i].match(format) !== null) return false
                if (isErrorOnFirstCharacter(value[i])) return false
                if (isSpaceOrApostropheDoubledOrCombinedInString(value[i])) return false
            }
        } else return false
    } else if (type === 'string') {
        if (value) {
            if (value.match(format) !== null) return false
            if (isErrorOnFirstCharacter(value)) return false
            if (isSpaceOrApostropheDoubledOrCombinedInString(value)) return false
        } else return false
    }
    return true
}

/**
 * @description Looks if birthplace contains only digits
 * @param {string} birthPlace string birthplace
 * @returns {boolean}
 */
const isFormatBirthPlaceValid = (birthPlace: string) => {
    const format = /^[0-9AB]+$/
    if (birthPlace && birthPlace.match(format)) return true
    return false
}
class Connector {
    _available: boolean

    _connector: any

    _currentEmail: any

    constructor() {
        this._connector = null
        this._available = false
        this._currentEmail = false
        this.initialize()
    }

    /**
     * @description get Connector
     * @return {Connector}
     * @memberof Connector
     */
    getConnector() {
        return this._connector
    }

    /**
     * @description initialize Connector
     * @returns {void}
     * @memberof Connector
     */
    initialize() {
        try {
            let dcparameters = dcparametersStaging
            if (window._env_.REACT_APP_TTD_ENV === 'production') dcparameters = dcparametersProduction

            fetch('https://localhost.icanopee.net:9982/remotecommand/registerDcParameter', {
                method: 'POST',
                body: JSON.stringify({
                    s_dcparameters64: dcparameters.license,
                }),
            }).catch(() => undefined)

            this._connector = new INSIConnector(
                window._env_.REACT_APP_INSI_SERVER_DOMAIN,
                dcparameters.license,
                1,
                !['production', 'preprod', 'staging'].includes(window._env_.NODE_ENV),
            )
            this._available = true
        } catch (error) {
            throw new TTDError("Erreur impossible d'initialiser le connecteur INSi")
        }
    }

    /**
     * @async
     * @description Detect if the dmp connector is installed and running
     * @returns {Promise<boolean>}
     * @memberof Connector
     */
    checkDmpConnectJsServiceAvailable() {
        return this._connector.checkDmpConnectJsServiceAvailable()
    }

    /**
     * @async
     * @description open Insi session
     * @returns {void}
     * @memberof Connector
     */
    async openSession() {
        if (!this._available) throw new TTDError('Connecteur indisponible')
        try {
            if (!this._connector.lpsInfos) {
                const config = await getIdsConfig()

                const hcsId = localStorage.getItem('currentHcs')
                if (!hcsId) throw new Error('Cannot initialize Insi connector, hcs id not found in local storage.')

                const healthCareService = await getIdsHealthcareservicesHcsId(hcsId)

                const billingNumber = healthCareService?.finessNum || window._env_.REACT_APP_INSI_BILLING_NUMBER

                let authNumber = window._env_.REACT_APP_INSI_AUTH_NUMBERW
                if (window.navigator.platform === 'MacIntel') {
                    authNumber = window._env_.REACT_APP_INSI_AUTH_NUMBERM
                }
                // @ts-expect-error TS(2339) FIXME: Property 'userAgentData' does not exist on type 'N... Remove this comment to see the full error message
                if (window.navigator.userAgentData && window.navigator.userAgentData.platform === 'macOS') {
                    authNumber = window._env_.REACT_APP_INSI_AUTH_NUMBERM
                }

                this._connector.setLpsInfos(authNumber, billingNumber, config?.insParams, packageVersion)
            }
            await this._connector.openSession()
        } catch (error) {
            console.error(`[Connector Session Opening]`, error)
            throw new TTDError("Impossible d'ouvrir une session")
        }
    }

    /**
     * @async
     * @description close Insi session
     * @returns {void}
     * @memberof Connector
     */
    async closeSession() {
        if (!this._available) throw new TTDError('Connecteur indisponible')

        try {
            await this._connector.closeSession()
        } catch (error) {
            throw new TTDError('Erreur lors de la fermeture de la session')
        }
    }

    /**
     * Delete the session on login to have fresh billing data
     */
    deleteSession() {
        if (this._connector) this._connector.lpsInfos = null
    }

    /**
     * @async
     * @description get all readers
     * @return {Array<Reader>}
     * @memberof Connector
     */
    async getCardReaders() {
        const finalReaders: any = []

        if (!this._available) {
            throw new TTDError('Connecteur indisponible')
        }

        try {
            const readers = await this._connector.getCardReaders()

            if (readers && readers.length) {
                readers.forEach((reader: any) => {
                    finalReaders.push({
                        label: reader.name,
                        value: {
                            index: reader.index,
                            name: reader.s_name,
                            cardInside: reader.cardInside,
                        },
                    })
                })
            }

            return finalReaders
        } catch (error) {
            throw new TTDError('Erreur impossible de lire les lecteurs')
        }
    }

    /**
     * @async
     * @description read selected vital card and returns patients - ws_Ins2
     * @param {*} index index of vital reader
     * @param {*} name name of vital reader
     * @param {Function} callback callback called by monitoring if the card is removed
     * @returns {Array<Object>} patients
     * @memberof Connector
     */
    async readVitalCard(index: any, name: any, callback: any) {
        const finalPatients: any = []
        try {
            await this._connector.setVitaleReader(index, name, callback)
            const results = await this._connector.readVitaleCard()
            if (results && [3, 4].includes(results.statusCard)) {
                if (results && results.data && results.data.length) {
                    results.data.forEach((patient: any) => {
                        let label = ''
                        let patientName = `${patient.lastName}`
                        if (
                            patient.lastName !== '' &&
                            patient.birthName !== '' &&
                            patient.lastName !== patient.birthName
                        ) {
                            patientName = `${patient.lastName} (${patient.birthName})`
                        } else if (patient.lastName !== '' && patient.birthName) {
                            patientName = `${patient.birthName}`
                        }
                        label = patientName ? `${patientName} ${patient.firstName}` : `${patient.firstName}`
                        finalPatients.push({
                            label,
                            value: patient,
                        })
                    })
                }
            }
            return finalPatients
        } catch (error) {
            throw new TTDError('Erreur impossible de lire la carte vitale')
        }
    }

    /**
     * @async
     * @description read selected vital card and returns patients - ws_Ins1
     * @param {Number} index index of vital reader
     * @returns {Array<Object>} patients
     * @memberof Connector
     */
    async searchPatientWithVitaleCardIndex(index: any, rawResponse = false) {
        try {
            return await this._connector.searchPatientWithVitaleCardIndex(index, rawResponse)
        } catch (error) {
            if (typeof error === 'number') {
                return error
            }
            throw new TTDError("Erreur impossible de récupérer l'identité INS")
        }
    }

    /**
     * @async
     * @description search patient with some informations and without vitale card
     * @param {String} birthName
     * @param {Array} firstNames
     * @param {String} sex we nend to transform string into number
     * @param {String} birthdate YYYY-MM-DD
     * @param {String} birthplace postal code
     * @returns {Promise<Object>}
     * @memberof Connector
     */
    async searchPatientWithoutVitaleCard(
        birthName: Patient['birthName'],
        firstNames: Patient['firstNameList'],
        sex: Patient['sex'],
        birthdate: Patient['birthDate'],
        birthplace: Patient['birthCityCode'],
        rawResponse = false,
    ) {
        try {
            let computedSex: 1 | 2 | 3 | undefined
            if (sex?.toLowerCase() === 'i') computedSex = 1
            if (sex?.toLowerCase() === 'm') computedSex = 2
            if (sex?.toLowerCase() === 'f') computedSex = 3
            const computedBirthName = birthName ? cleanAccentsAndDiacritics(birthName.toUpperCase()) : null

            if (computedBirthName && !isFormatValid('string', birthName)) {
                return {
                    error: -1,
                    label: 'Nom de naissance',
                }
            }

            const computedFirstNames = firstNames
                ? firstNames.map((el: any) => {
                      const val = el?.__typename === 'Element' ? el?.value?.toUpperCase() : el?.toUpperCase()
                      return cleanAccentsAndDiacritics(val)
                  })
                : null

            if (computedFirstNames && !isFormatValid('array', computedFirstNames)) {
                return {
                    error: -1,
                    label: '1er Prénom de naissance et autres prénoms de naissance',
                }
            }

            if (birthplace) {
                if (!isFormatBirthPlaceValid(birthplace)) {
                    return {
                        error: -2,
                    }
                }
            }

            return await this._connector.searchPatient(
                computedBirthName,
                computedFirstNames ? computedFirstNames.join(' ') : undefined,
                computedSex,
                birthdate,
                birthplace,
                rawResponse,
            )
        } catch (error) {
            // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
            throw new TTDError(`Erreur impossible de récupérer l'identité INS: ${error.message}`)
        }
    }

    /**
     * @async
     * @description read selected CPx Card using PIN
     * @param {Number} index index of cps reader
     * @param {String} name name of cps reader
     * @param {String} pin cps card pin
     * @returns {void}
     * @memberof Connector
     */
    async readCPxCard(index: any, name: any, pin: any): Promise<void | TTDError> {
        try {
            await this._connector.setCpsReader(index, name)
            await this._connector.readCPxCard(pin)
        } catch (error) {
            let errorMessage = 'Erreur lors de la lecture de la carte CPS'
            if (error === 'NO_CPS_READER') errorMessage = 'Lecteur de carte incorrect'
            else if (error === 'WRONG_PIN') errorMessage = 'Code pin incorrect'
            else if (error === 'CARD_BLOCKED') errorMessage = 'Carte bloquée'
            else if (error === 'NO_CARD') errorMessage = 'Aucune carte'
            else if (error === 'PIN_REQUIRED') errorMessage = 'Veuillez fournir un code pin'
            throw new TTDError(errorMessage)
        }
    }

    /**
     * @async
     * @description get cps reader with parameters
     * @returns {void}
     * @memberof Connector
     */
    async getCpxReader(index: any, name: any): Promise<void | TTDError> {
        try {
            await this._connector.setCpsReader(index, name)
        } catch (error) {
            throw new TTDError('Un problème est survenue lors de la lecture de la carte CPS')
        }
    }

    /**
     * @async
     * @description get status from cps card
     * @returns {Number}
     * @memberof Connector
     */
    async getCpxStatus(): Promise<number | TTDError> {
        try {
            return await this._connector.getCpsStatus()
        } catch (error) {
            throw new TTDError('Un problème est survenue avec la carte CPS')
        }
    }

    /**
     * @async
     * @description
     * @param {Number} index
     * @param {String} name
     * @returns {Number}
     * @memberof Connector
     */
    async isCpxCardAvailable(index: any, name: any): Promise<number | TTDError> {
        try {
            await this.getCpxReader(index, name)
            return await this.getCpxStatus()
        } catch (error) {
            // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
            throw new TTDError(error)
        }
    }

    /**
     * @async
     * @description check INS Identity WS_INS3
     * @param {String} ins INS num
     * @param {String} key clé NIR
     * @param {String} oid oid
     * @param {String} lastName patient last name
     * @param {String} firstNames patient first names
     * @param {String} sex
     * @param {Date} birthday
     * @param {String} birthplace postal code
     * @returns {Boolean}
     * @memberof Connector
     */
    async checkPatientIdentity(
        ins: any,
        key: any,
        oid: any,
        lastName: any,
        firstNames: string[],
        sex: any,
        birthday: any,
        birthplace: any,
        rawResponse = false,
    ) {
        let formatSex = 1
        if (sex.toUpperCase() === 'M') formatSex = 2
        else if (sex.toUpperCase() === 'F') formatSex = 3

        try {
            return await this._connector.checkPatient(
                ins,
                key,
                oid,
                cleanAccentsAndDiacritics(lastName.toUpperCase()),
                firstNames.join(' '),
                formatSex,
                birthday,
                birthplace,
                rawResponse,
            )
        } catch (error) {
            throw new TTDError("Erreur impossible de vérifier l'identité INS du patient")
        }
    }

    /**
     * @description create batches to verify
     * @param {User} user current user to get the correct unencrypted data
     * @param {Array} patients list of patients
     * @memberof Connector
     */
    async sendBatches(user: any, patients: any) {
        const storedData = this.getStoredData(user)

        await this.openSession()
        await this.getCardReaders()
        await this.readCPxCard(storedData.cpsCard.index, storedData.cpsCard.name, storedData.pin)
        const res = await this._connector.sendBatches(patients)

        return res
    }

    /**
     * @description check the result of batches
     * @param {User} user current user to get the correct unencrypted data
     * @param {Array} batchesId list of batches to check
     * @param {Object} submitedInsNums list of patients per batch
     */
    async checkBatches(user: any, batchesId: any, submitedInsNums: any) {
        const storedData = this.getStoredData(user)

        await this.openSession()
        await this.getCardReaders()
        await this.readCPxCard(storedData.cpsCard.index, storedData.cpsCard.name, storedData.pin)
        const res = await this._connector.checkBatches(batchesId, submitedInsNums)

        return res
    }

    /**
     * @description get stored data in localStorage
     * @param {User} user current user to get the correct unencrypted data
     * @returns {Object} connectors informations
     * @memberof Connector
     */
    getStoredData(user: GetCurrentUserResponseIds) {
        if (!this._available) throw new TTDError('Connecteur indisponible')
        const storedData = localStorage.getItem('insi')
        if (storedData !== null && typeof storedData !== 'undefined') {
            const jsonData = JSON.parse(storedData)
            if (jsonData[user.username]) {
                const res = jsonData[user.username]
                res.pin = CryptoJS.AES.decrypt(res.pin, user.id).toString(CryptoJS.enc.Utf8)
                return res
            }
        }
        return null
    }

    /**
     * @description set connector data in localStorage
     * @param {User} user current user to get the correct unencrypted data
     * @param {Object} data the new data information on connector
     * @memberof Connector
     */
    setStoredData(user: GetCurrentUserResponseIds, data: any) {
        if (!this._available) throw new TTDError('Connecteur indisponible')
        const storedData = localStorage.getItem('insi')
        let jsonData
        if (storedData === null || typeof storedData === 'undefined') {
            jsonData = {}
        } else jsonData = JSON.parse(storedData)
        const pin = CryptoJS.AES.encrypt(data.pin, user.id).toString()
        jsonData[user.username] = {
            ...data,
            pin,
        }
        localStorage.setItem('insi', JSON.stringify(jsonData))
    }

    /**
     * @description set callback to clean data when CPS is removed
     * @param {Function} callback the callback
     * @memberof Connector
     */
    setMonitoringCpxCallback(callback: any) {
        this._connector.monitoringCpxCallback = callback
    }
}

const insi = new Connector()

try {
    const storedData = localStorage.getItem('insi')
    if (storedData !== null && typeof storedData !== 'undefined') {
        const jsonData = JSON.parse(storedData)

        Object.keys(jsonData).forEach((email) => {
            if (jsonData[email].expire < Date.now()) delete jsonData[email]
        })

        localStorage.setItem('insi', JSON.stringify(jsonData))
    }
} catch (error) {
    console.log(error)
}

export default insi
